import {
  ArrowLeft,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChefHat,
  GalleryHorizontalEnd,
  List,
  Pencil,
  Printer,
  Rows3,
} from 'lucide-react'
import { useEffect, useEffectEvent, useId, useRef, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import {
  ContentsList,
  ContentsPicture,
  Cover,
  Dedication,
  PictureImage,
  Page,
  RecipePicture,
  RecipeText,
  SectionPage,
} from '@/components/cookbook/Pages'
import { FormError } from '@/components/FormError'
import { PageTurn, type Leaf as TurnLeaf } from '@/components/PageTurn'
import { page as pageWidth } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { splitIngredient } from '@/lib/cookbook/ingredients'
import { coverOf, firstPicture, leavesOf, onePagesOf, type Leaf, type OnePage } from '@/lib/cookbook/pages'
import { useCookbook, type Cookbook, type CookbookPage as RecipePage } from '@/lib/cookbooks'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { useIsWide } from '@/lib/platform'
import { lines } from '@/lib/recipes'
import { remember, remembered } from '@/lib/remember'
import { cn } from '@/lib/utils'
import { PageLoader } from '@/components/Loader'

export function CookbookPage() {
  const { id = '' } = useParams()
  const cookbook = useCookbook(id)
  if (cookbook.isPending) return <PageLoader />
  if (cookbook.isError) return <FormError error={cookbook.error} />
  return <Reader cookbook={cookbook.data} />
}

// Where you are in the book: the cover, the dedication if there is one, the contents, then one stop per recipe, with a
// section's opening page before its first (lib/cookbook/pages.ts). It lives in the address, so a link opens the same
// recipe and the back button steps back through the pages.
const leafOf = (value: string | null, last: number) => {
  const n = Number(value)
  return Number.isInteger(n) && n >= 0 && n <= last ? n : 0
}

// Two ways to read it, the choice kept on this device. As a book its pages turn under the reader's finger, as the
// album storybook's do: on a large screen two pages at a time, on a phone a page at a time. Recipe by recipe it reads
// like a cooking app: one recipe at a time, its photo on top, swiped or tapped through.
const VIEWS = ['book', 'recipes'] as const
type View = (typeof VIEWS)[number]
const VIEW_NAMES: Record<View, string> = { book: 'Book', recipes: 'Recipe by recipe' }
const VIEW_KEY = 'gf.cookbook-view'

function Reader({ cookbook }: { cookbook: Cookbook }) {
  const [params, setParams] = useSearchParams()
  const [view, setView] = useState<View>(() => remembered(VIEW_KEY, VIEWS, 'book'))
  const wide = useIsWide()
  const count = cookbook.pages.length
  const leaves = leavesOf(cookbook)
  const last = leaves.length - 1
  const leaf = leafOf(params.get('p'), last)
  const go = (n: number) => {
    setParams(
      (p) => {
        if (n === 0) p.delete('p')
        else p.set('p', String(n))
        return p
      },
      { preventScrollReset: true },
    )
    window.scrollTo({ top: 0 })
  }
  const href = (n: number) => `/cookbook/${cookbook.id}${n ? `?p=${n}` : ''}`
  const home = useItemHome(cookbook)
  usePageFrame(home, {
    title: cookbook.title,
    back: { to: `/circle/${home}/recipes`, label: 'Back to recipes' },
  })
  const choose = (v: View) => {
    setView(v)
    remember(VIEW_KEY, v)
  }

  // The arrow keys turn the pages. A book on a phone turns a page at a time, so it listens for them itself.
  const onePage = view === 'book' && !wide
  useArrowKeys((by) => {
    if (!onePage && leaf + by >= 0 && leaf + by <= last) go(leaf + by)
  })

  const turn = { cookbook, leaves, leaf, go, href }
  return (
    <div className="flex flex-col gap-6 pt-4 pb-28 nav:pt-10 lg:pb-12">
      <Toolbar cookbook={cookbook} view={count ? view : null} choose={choose} />
      {count === 0 ? (
        <Empty cookbook={cookbook} />
      ) : view === 'recipes' ? (
        <Screens {...turn} />
      ) : wide ? (
        <BookSpreads {...turn} />
      ) : (
        <BookPages {...turn} />
      )}
    </div>
  )
}

function Toolbar({ cookbook, view, choose }: { cookbook: Cookbook; view: View | null; choose: (view: View) => void }) {
  return (
    <div className={cn(pageWidth, 'flex flex-wrap items-center gap-2')}>
      <Link
        to={`/circle/${cookbook.familyId}/recipes`}
        className="mr-auto hidden h-10 items-center gap-1.5 rounded-full bg-secondary px-4 text-sm font-medium transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 nav:inline-flex"
      >
        <ArrowLeft className="size-4" aria-hidden /> Recipes
      </Link>
      <span className="mr-auto nav:hidden" />
      {view && <ViewSwitch view={view} choose={choose} />}
      {cookbook.pages.length > 0 && (
        <Button asChild variant="outline" size="lg">
          <Link to={`/cookbook/${cookbook.id}/print`}>
            <Printer aria-hidden /> Print
          </Link>
        </Button>
      )}
      {cookbook.pages.length > 0 && (
        <Button asChild variant="outline" size="lg">
          <Link to={`/cookbook/${cookbook.id}/cards`}>
            <GalleryHorizontalEnd aria-hidden /> Cards
          </Link>
        </Button>
      )}
      {cookbook.mine && (
        <Button asChild variant="outline" size="lg">
          <Link to={`/cookbook/${cookbook.id}/edit`}>
            <Pencil aria-hidden /> Edit
          </Link>
        </Button>
      )}
    </div>
  )
}

const VIEW_ICONS = { book: BookOpen, recipes: Rows3 }

function ViewSwitch({ view, choose }: { view: View; choose: (view: View) => void }) {
  return (
    <div role="radiogroup" aria-label="How to read it" className="flex rounded-full bg-secondary p-1">
      {VIEWS.map((v) => {
        const Icon = VIEW_ICONS[v]
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={view === v}
            aria-label={VIEW_NAMES[v]}
            title={VIEW_NAMES[v]}
            onClick={() => choose(v)}
            className={cn(
              'flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-full px-2.5 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 sm:px-3',
              view === v ? 'bg-brand text-brand-foreground' : 'hover:bg-accent',
            )}
          >
            <Icon className="size-4" aria-hidden />
            <span className="hidden sm:inline">{VIEW_NAMES[v]}</span>
          </button>
        )
      })}
    </div>
  )
}

function Empty({ cookbook }: { cookbook: Cookbook }) {
  return (
    <div className={cn(pageWidth, 'flex flex-col items-center gap-8')}>
      <div className="w-full max-w-sm shadow-paper">
        <Cover {...coverOf(cookbook)} sizes="24rem" />
      </div>
      <p className="max-w-md text-center text-muted-foreground">
        {cookbook.mine
          ? 'Choose the recipes to go in it, and each one gets its own page.'
          : 'No recipes in this cookbook yet.'}
      </p>
      {cookbook.mine && (
        <Button asChild size="lg">
          <Link to={`/cookbook/${cookbook.id}/edit`}>Choose recipes</Link>
        </Button>
      )}
    </div>
  )
}

type Turn = {
  cookbook: Cookbook
  leaves: Leaf[]
  leaf: number
  go: (n: number) => void
  href: (n: number) => string
}

// ---- The book: pages that turn. Two at a time on a large screen, each leaf with a recipe's words on its front and the
// next recipe's picture on its back, as a printed book has them.

function BookSpreads({ cookbook, leaves, leaf, go, href }: Turn) {
  const last = leaves.length - 1
  const sizes = '(min-width: 1536px) 38rem, 40vw'
  const left = (at: Leaf): ReactNode => {
    if (at.kind === 'contents') return <ContentsPicture cookbook={cookbook} sizes={sizes} />
    if (at.kind === 'recipe')
      return <RecipePicture page={cookbook.pages[at.index]!} folio={at.folio - 1} sizes={sizes} />
    return <Page side="left">{null}</Page>
  }
  const right = (at: Leaf): ReactNode => {
    if (at.kind === 'cover') return <Cover {...coverOf(cookbook)} sizes={sizes} />
    if (at.kind === 'dedication') return <Dedication text={cookbook.dedication} />
    if (at.kind === 'contents') return <ContentsList cookbook={cookbook} href={href} />
    if (at.kind === 'section') return <SectionPage section={at.section} folio={at.folio} />
    return <RecipeText page={cookbook.pages[at.index]!} folio={at.folio} running={cookbook.title} />
  }
  const book: TurnLeaf[] = leaves.map((at, n) => ({
    front: right(at),
    back: n < last ? left(leaves[n + 1]!) : undefined,
    label: where(cookbook, at),
  }))
  return (
    <section aria-label={cookbook.title} className="flex flex-col gap-4 px-8" data-testid="cookbook-book">
      {/* As big as fits: the full width, or less so the whole spread stays on screen. */}
      <div className="relative mx-auto w-full max-w-[min(100%,calc((100svh-12rem)*1.6))] px-10 xl:px-12">
        <div className="absolute inset-y-0 left-0 flex items-center">
          <TurnButton side="back" disabled={leaf === 0} onClick={() => go(leaf - 1)} />
        </div>
        <PageTurn leaves={book} open={leaf} onTurn={go} spread />
        <div className="absolute inset-y-0 right-0 flex items-center">
          <TurnButton side="next" disabled={leaf === last} onClick={() => go(leaf + 1)} />
        </div>
      </div>
      <p className="flex justify-center gap-3 text-center text-sub text-muted-foreground">
        <span aria-live="polite">{where(cookbook, leaves[leaf]!)}</span>
        <span aria-hidden>·</span>
        <span>Drag a page to turn it</span>
      </p>
    </section>
  )
}

// On a phone, a page at a time: the cover, the contents, then each recipe's picture and, turning it, its words, set
// large enough to cook from.
function BookPages({ cookbook, leaves, leaf, go, href }: Turn) {
  const pages = onePagesOf(cookbook)
  const first = (n: number) =>
    Math.max(
      0,
      pages.findIndex((x) => x.leaf === n),
    )
  // The place in the address is a stop in the book; which of its pages is open is kept here.
  const [at, setAt] = useState({ leaf, page: first(leaf) })
  const open = at.leaf === leaf ? at.page : first(leaf)
  const turn = (to: number) => {
    const page = Math.max(0, Math.min(pages.length - 1, to))
    const target = pages[page]!.leaf
    setAt({ leaf: target, page })
    if (target !== leaf) go(target)
  }
  useArrowKeys((by) => turn(open + by))
  const contents = leaves.findIndex((x) => x.kind === 'contents')
  const face = (page: OnePage): ReactNode => {
    const stop = leaves[page.leaf]!
    if (stop.kind === 'cover') return <Cover {...coverOf(cookbook)} sizes="90vw" />
    if (stop.kind === 'dedication') return <Dedication text={cookbook.dedication} />
    if (stop.kind === 'section') return <SectionPage section={stop.section} />
    if (stop.kind === 'contents')
      return (
        <div className="cb-phone-page">
          <h2 className="text-center font-book text-[2.25rem] leading-tight font-normal tracking-normal">Contents</h2>
          <ContentsLinks cookbook={cookbook} leaves={leaves} href={href} />
        </div>
      )
    const recipe = cookbook.pages[stop.index]!
    if (page.kind === 'picture')
      return (
        <div className="cb-phone-picture">
          <PictureImage picture={recipe.picture!} alt={recipe.recipe.title} sizes="90vw" />
        </div>
      )
    return (
      <div className="cb-phone-page">
        <RecipeWords cookbook={cookbook} page={recipe} />
      </div>
    )
  }
  const book: TurnLeaf[] = pages.map((page) => ({
    front: face(page),
    label:
      page.kind === 'picture'
        ? `${where(cookbook, leaves[page.leaf]!)}, the picture`
        : where(cookbook, leaves[page.leaf]!),
  }))
  return (
    <div className="flex flex-col items-center gap-3 px-4" data-testid="cookbook-book">
      <PageTurn
        leaves={book}
        open={open}
        onTurn={turn}
        className="aspect-[4/5] w-[min(100%,calc((100svh-var(--tabs-clear)-10rem)*0.8))]"
      />
      <p className="text-xs text-muted-foreground">Swipe, or tap the edge of the page</p>
      <PagesNav
        label={stopName(cookbook, leaves[leaf]!)}
        back={open > 0 ? () => turn(open - 1) : undefined}
        next={open < pages.length - 1 ? () => turn(open + 1) : undefined}
        contents={() => go(contents)}
      />
    </div>
  )
}

function where(cookbook: Cookbook, at: Leaf) {
  if (at.kind === 'cover') return 'Cover'
  if (at.kind === 'dedication') return 'Dedication'
  if (at.kind === 'contents') return 'Contents'
  if (at.kind === 'section') return at.section.title
  return `${cookbook.pages[at.index]!.recipe.title}, ${at.index + 1} of ${cookbook.pages.length}`
}

function TurnButton({ side, disabled, onClick }: { side: 'back' | 'next'; disabled: boolean; onClick: () => void }) {
  const Icon = side === 'back' ? ChevronLeft : ChevronRight
  return (
    <Button
      variant="outline"
      size="icon"
      className="relative z-10 size-14 shrink-0 bg-background shadow-paper"
      aria-label={side === 'back' ? 'Previous page' : 'Next page'}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className="size-6" aria-hidden />
    </Button>
  )
}

// ---- Phones and tablets: one recipe at a time

function Screens({ cookbook, leaves, leaf, go, href }: Turn) {
  const last = leaves.length - 1
  const at = leaves[leaf]!
  const contents = leaves.findIndex((x) => x.kind === 'contents')
  const swipe = useSwipe((dir) => {
    if (dir === 'next' && leaf < last) go(leaf + 1)
    if (dir === 'back' && leaf > 0) go(leaf - 1)
  })
  return (
    <div {...swipe}>
      {at.kind === 'cover' && (
        <div className={cn(pageWidth, 'flex flex-col items-center gap-6')}>
          <div className="w-full max-w-md shadow-paper">
            <Cover {...coverOf(cookbook)} sizes="(min-width: 640px) 28rem, 90vw" />
          </div>
          <Button size="lg" onClick={() => go(1)}>
            <List aria-hidden /> Open the book
          </Button>
        </div>
      )}
      {at.kind === 'dedication' && (
        <div className={cn(pageWidth, 'flex flex-col items-center')}>
          <div className="w-full max-w-md shadow-paper">
            <Dedication text={cookbook.dedication} />
          </div>
        </div>
      )}
      {at.kind === 'contents' && <PhoneContents cookbook={cookbook} leaves={leaves} href={href} />}
      {at.kind === 'section' && (
        <div className={cn(pageWidth, 'flex flex-col items-center')}>
          <div className="w-full max-w-md shadow-paper">
            <SectionPage section={at.section} />
          </div>
        </div>
      )}
      {at.kind === 'recipe' && <PhoneRecipe cookbook={cookbook} page={cookbook.pages[at.index]!} />}
      <PagesNav
        label={stopName(cookbook, at)}
        back={leaf > 0 ? () => go(leaf - 1) : undefined}
        next={leaf < last ? () => go(leaf + 1) : undefined}
        contents={() => go(contents)}
      />
    </div>
  )
}

// Where the reader is, in the bar at the foot of the page.
function stopName(cookbook: Cookbook, at: Leaf) {
  if (at.kind === 'recipe') return `${at.index + 1} of ${cookbook.pages.length}`
  if (at.kind === 'section') return at.section.title
  return 'Contents'
}

function PagesNav({
  label,
  back,
  next,
  contents,
}: {
  label: string
  back?: () => void
  next?: () => void
  contents: () => void
}) {
  return (
    <nav
      aria-label="Pages"
      className="fixed inset-x-0 bottom-[calc(var(--tabs-clear)+0.75rem-var(--vv-below))] z-10 mx-auto flex w-fit items-center gap-1 rounded-full bg-background/95 p-1.5 shadow-float backdrop-blur nav:bottom-6"
    >
      <Button variant="ghost" size="icon" aria-label="Previous page" disabled={!back} onClick={back}>
        <ChevronLeft className="size-5" aria-hidden />
      </Button>
      <Button variant="ghost" className="h-11 px-4" onClick={contents} aria-label="Contents">
        <List aria-hidden />
        <span aria-live="polite">{label}</span>
      </Button>
      <Button variant="ghost" size="icon" aria-label="Next page" disabled={!next} onClick={next}>
        <ChevronRight className="size-5" aria-hidden />
      </Button>
    </nav>
  )
}

function PhoneContents({
  cookbook,
  leaves,
  href,
}: {
  cookbook: Cookbook
  leaves: Leaf[]
  href: (n: number) => string
}) {
  const picture = firstPicture(cookbook)
  return (
    <div className={cn(pageWidth, 'flex flex-col gap-6')}>
      <h2 className="text-center font-book text-[2.5rem] leading-tight font-normal tracking-normal">Contents</h2>
      <ContentsLinks cookbook={cookbook} leaves={leaves} href={href} />
      {!picture && <span className="sr-only">This cookbook has no photos yet.</span>}
    </div>
  )
}

// The recipes in order, each a link to its place in the book, under their sections when the book has them.
function ContentsLinks({
  cookbook,
  leaves,
  href,
}: {
  cookbook: Cookbook
  leaves: Leaf[]
  href: (n: number) => string
}) {
  return (
    <ol className="flex flex-col divide-y divide-border">
      {leaves.map((leaf, n) => {
        if (leaf.kind === 'section')
          return (
            <li
              key={`section-${leaf.section.first}`}
              className="pt-6 pb-2 text-xs font-semibold tracking-[0.3em] text-muted-foreground uppercase first:pt-0"
            >
              {leaf.section.title}
            </li>
          )
        if (leaf.kind !== 'recipe') return null
        const page = cookbook.pages[leaf.index]!
        return (
          <li key={page.recipe.id}>
            <Link
              to={href(n)}
              className="flex items-center gap-4 py-3 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <span className="size-14 shrink-0 overflow-hidden rounded-xl bg-secondary">
                {page.picture && (
                  <span className="block size-full [&_img]:size-full [&_img]:object-cover">
                    <PictureImage picture={page.picture} alt="" sizes="56px" />
                  </span>
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-book text-xl leading-snug">{page.recipe.title}</span>
                {page.recipe.from && (
                  <span className="truncate text-sub text-muted-foreground">From {page.recipe.from.name}</span>
                )}
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </Link>
          </li>
        )
      })}
    </ol>
  )
}

function PhoneRecipe({ cookbook, page }: { cookbook: Cookbook; page: RecipePage }) {
  const { recipe, picture } = page
  return (
    <article className="flex flex-col gap-6">
      {picture && (
        <div className="mx-auto w-full max-w-2xl overflow-hidden sm:rounded-[1.75rem] [&_img]:aspect-[4/5] [&_img]:max-h-[70svh] [&_img]:w-full [&_img]:object-cover">
          <PictureImage picture={picture} alt={recipe.title} sizes="(min-width: 672px) 42rem, 100vw" />
        </div>
      )}
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-5 sm:px-8">
        <RecipeWords cookbook={cookbook} page={page} />
      </div>
    </article>
  )
}

// A recipe's words as a phone shows them, recipe by recipe and on the book's pages alike.
function RecipeWords({ cookbook, page }: { cookbook: Cookbook; page: RecipePage }) {
  const { recipe } = page
  const ingredients = lines(recipe.ingredients).map(splitIngredient)
  const steps = lines(recipe.steps)
  const notes = recipe.notes.trim()
  const id = useId()
  return (
    <>
      <header className="flex flex-col gap-3 border-b border-foreground pb-4">
        <p className="text-[0.6875rem] tracking-[0.3em] text-muted-foreground uppercase">{cookbook.title}</p>
        <h2 className="font-book text-[2.25rem] leading-[1.08] font-normal tracking-normal text-balance">
          {recipe.title}
        </h2>
        <p className="flex flex-wrap gap-x-6 gap-y-1 text-sub text-muted-foreground">
          {recipe.from && (
            <span>
              From <span className="font-semibold text-foreground">{recipe.from.name}</span>
            </span>
          )}
          {recipe.book && (
            <span>
              Book <span className="font-semibold text-foreground">{recipe.book.title}</span>
            </span>
          )}
        </p>
      </header>
      {ingredients.length > 0 && (
        <section aria-labelledby={`${id}-ingredients`} className="flex flex-col gap-3">
          <h3 id={`${id}-ingredients`} className="text-base font-semibold">
            Ingredients
          </h3>
          <ul className="flex flex-col divide-y divide-border">
            {ingredients.map((x, i) => (
              <li key={i} className="grid grid-cols-[5.5rem_1fr] gap-3 py-2">
                {x.amount ? (
                  <>
                    <span className="text-muted-foreground">{x.amount}</span>
                    <span>{x.item}</span>
                  </>
                ) : (
                  <span className="col-span-2">{x.item}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {steps.length > 0 && (
        <section aria-labelledby={`${id}-steps`} className="flex flex-col gap-3">
          <h3 id={`${id}-steps`} className="text-base font-semibold">
            Preparation
          </h3>
          <ol className="flex flex-col gap-4">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                {steps.length > 1 && <span className="w-5 shrink-0 font-bold">{i + 1}.</span>}
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
      {notes && (
        <section aria-labelledby={`${id}-notes`} className="flex flex-col gap-2">
          <h3 id={`${id}-notes`} className="text-base font-semibold">
            Notes
          </h3>
          <p className="font-book text-lg whitespace-pre-line italic">{notes}</p>
        </section>
      )}
      <div className="flex flex-wrap gap-2">
        <Button asChild size="lg">
          <Link to={`/recipe/${recipe.id}/cook`}>
            <ChefHat aria-hidden /> Cook this
          </Link>
        </Button>
        <Button asChild variant="outline" size="lg">
          <Link to={`/recipe/${recipe.id}`}>Open the recipe</Link>
        </Button>
      </div>
    </>
  )
}

// The left and right arrow keys, unless you're typing somewhere.
function useArrowKeys(onArrow: (by: 1 | -1) => void) {
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select, [contenteditable]')) return
    if (e.key === 'ArrowRight') onArrow(1)
    if (e.key === 'ArrowLeft') onArrow(-1)
  })
  useEffect(() => {
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

// A sideways swipe turns the page; anything mostly up and down is left to scroll.
function useSwipe(onSwipe: (dir: 'next' | 'back') => void) {
  const start = useRef<{ x: number; y: number } | null>(null)
  return {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0]
      start.current = e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY } : null
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const from = start.current
      const t = e.changedTouches[0]
      start.current = null
      if (!from || !t) return
      const dx = t.clientX - from.x
      const dy = t.clientY - from.y
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) onSwipe(dx < 0 ? 'next' : 'back')
    },
  }
}
